/** Ultimo pedido solo en frontend (sin backend). Clave por negocio: `last_order_<businessId>`. */

const LEGACY_STORAGE_KEY = 'last_order';

function storageKey(businessId) {
  return `last_order_${String(businessId ?? '')}`;
}

/** Ids/slugs posibles para el mismo local (evita perder el pedido al entrar por slug o UUID). */
export function collectBusinessStorageAliases(businessId, business = null) {
  const out = [];
  const push = (v) => {
    const s = String(v ?? '').trim();
    if (s && !out.includes(s)) out.push(s);
  };
  push(businessId);
  push(business?.id);
  push(business?.slug);
  push(business?.business?.slug);
  push(business?.business?.id);
  return out;
}

function normalizeOrder(payload, businessId) {
  const out = {
    businessId: String(payload.businessId ?? businessId ?? ''),
    businessName: String(payload.businessName ?? ''),
    items: Array.isArray(payload.items) ? payload.items : [],
    serviceType: String(payload.serviceType ?? payload.service_type ?? ''),
    total: typeof payload.total === 'number' ? payload.total : Number(payload.total) || 0,
    createdAt: payload.createdAt || new Date().toISOString(),
  };
  const cash = payload.cash_amount;
  if (cash != null && Number.isFinite(Number(cash)) && Number(cash) > 0) {
    out.cash_amount = Math.round(Number(cash));
  }
  if (payload.paymentMethod) out.paymentMethod = String(payload.paymentMethod);
  if (payload.tableNumber) out.tableNumber = String(payload.tableNumber);
  if (payload.address) out.address = String(payload.address);
  if (payload.customerName) out.customerName = String(payload.customerName);
  if (payload.customerPhone) out.customerPhone = String(payload.customerPhone);
  if (payload.markerPosition && typeof payload.markerPosition === 'object') {
    out.markerPosition = payload.markerPosition;
  }
  return out;
}

function parseStoredOrder(raw, allowedIds) {
  if (!raw) return null;
  try {
    const data = JSON.parse(raw);
    if (!data || typeof data !== 'object') return null;
    if (!Array.isArray(data.items) || data.items.length === 0) return null;
    if (data.businessId && allowedIds.length && !allowedIds.includes(String(data.businessId))) {
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

/** Migra `last_order` global si coincide con este negocio (una sola vez por lectura). */
function tryMigrateLegacy(businessId, aliases = []) {
  if (!businessId) return null;
  try {
    const raw = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || typeof data !== 'object') return null;
    const allowed = collectBusinessStorageAliases(businessId, null).concat(aliases.map(String));
    if (data.businessId && !allowed.includes(String(data.businessId))) return null;
    if (!Array.isArray(data.items) || data.items.length === 0) return null;
    const key = storageKey(businessId);
    const normalized = normalizeOrder(data, businessId);
    localStorage.setItem(key, JSON.stringify(normalized));
    localStorage.removeItem(LEGACY_STORAGE_KEY);
    return normalized;
  } catch {
    return null;
  }
}

export function readLastOrderForBusiness(businessId, business = null) {
  if (!businessId && !business) return null;
  const aliases = collectBusinessStorageAliases(businessId, business);
  if (!aliases.length) return null;
  try {
    for (const id of aliases) {
      const raw = localStorage.getItem(storageKey(id));
      const data = parseStoredOrder(raw, aliases);
      if (data) return data;
    }
    const migrated = tryMigrateLegacy(aliases[0], aliases);
    if (migrated && Array.isArray(migrated.items) && migrated.items.length > 0) {
      return migrated;
    }
    return null;
  } catch {
    return null;
  }
}

export function saveLastOrder(order, businessId, business = null) {
  const aliases = collectBusinessStorageAliases(businessId ?? order?.businessId, business);
  const bid = aliases[0] || String(businessId ?? order?.businessId ?? '');
  if (!bid) return null;
  const toSave = normalizeOrder(order, bid);
  toSave.businessId = bid;
  const payload = JSON.stringify(toSave);
  for (const id of aliases.length ? aliases : [bid]) {
    localStorage.setItem(storageKey(id), payload);
  }
  try {
    const legacyRaw = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (legacyRaw) {
      const legacy = JSON.parse(legacyRaw);
      if (legacy && typeof legacy === 'object' && aliases.includes(String(legacy.businessId))) {
        localStorage.removeItem(LEGACY_STORAGE_KEY);
      }
    }
  } catch {
    // ignore
  }
  return toSave;
}

export function clearLastOrder(businessId, business = null) {
  const aliases = collectBusinessStorageAliases(businessId, business);
  for (const id of aliases) {
    localStorage.removeItem(storageKey(id));
  }
}
